import { useCallback, useEffect, useMemo, useState } from 'react';
import { type StudioSidePanelContextProps, useVirtualization } from 'venn-components';
import { useRecoilCallback } from 'recoil';
import { selectedBlocksState, virtualScrollRef } from 'venn-state';
import { useScrollToBlock } from './useScrollToBlock';

/** Logic for selecting */
const useSelectionSystem = (): StudioSidePanelContextProps => {
  const scrollToBlock = useScrollToBlock();
  const [scrollToRefId, setScrollToRefId] = useState<string | undefined>();

  /** Workaround so that we scroll to the block after it has been inserted */
  useEffect(() => {
    if (scrollToRefId !== undefined) {
      scrollToBlock(scrollToRefId);
      setScrollToRefId(undefined);
    }
  }, [scrollToRefId, scrollToBlock]);

  const onSelectBlock: StudioSidePanelContextProps['onSelectBlock'] = useRecoilCallback(
    ({ set }) =>
      (refId, { scrollIntoView, isGlobal }) => {
        if (isGlobal) {
          set(selectedBlocksState, []);
        } else {
          if (!refId) {
            return;
          }
          set(selectedBlocksState, [refId]);
          if (scrollIntoView) {
            setScrollToRefId(refId);
          }
        }

        triggerResizeEvent();
      },
    [],
  );

  const onSelectGlobal: StudioSidePanelContextProps['onSelectGlobal'] = useCallback(
    (scrollToTop) => {
      onSelectBlock(undefined, { scrollIntoView: false, isGlobal: true });
      if (scrollToTop) {
        document.getElementById('report-content-container')?.scrollIntoView();
      }
    },
    [onSelectBlock],
  );

  const isVirtualized = useVirtualization();
  const scrollToSelectedPage = useRecoilCallback(
    ({ snapshot }) =>
      async (selectedPageIndex: number) => {
        if (isVirtualized) {
          const ref = await snapshot.getPromise(virtualScrollRef);
          ref?.current?.scrollToIndex({
            index: selectedPageIndex,
            align: 'center',
          });
        } else {
          document.getElementById(`page-${selectedPageIndex}`)?.scrollIntoView({ behavior: 'smooth' });
        }
      },
    [isVirtualized],
  );

  const onSelectPage: StudioSidePanelContextProps['onSelectPage'] = useCallback(
    (pageIndex, options) => {
      scrollToSelectedPage(pageIndex);
      if (options?.selectBlockId) {
        onSelectBlock(options.selectBlockId, { scrollIntoView: false });
      } else {
        onSelectGlobal();
      }
    },
    [onSelectBlock, onSelectGlobal, scrollToSelectedPage],
  );

  return useMemo(
    () => ({
      onSelectBlock,
      onSelectGlobal,
      onSelectPage,
    }),
    [onSelectBlock, onSelectGlobal, onSelectPage],
  );
};

export default useSelectionSystem;

export const triggerResizeEvent = () => {
  setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
};
